import React from 'react';

const CARD_STYLES = [
  { accent: '#6366f1', bg: '#eef2ff', icon: '🎵' },
  { accent: '#f59e0b', bg: '#fffbeb', icon: '🎶' },
  { accent: '#10b981', bg: '#ecfdf5', icon: '💰' },
  { accent: '#3b82f6', bg: '#eff6ff', icon: '📈' },
  { accent: '#ef4444', bg: '#fef2f2', icon: '📉' },
];

/**
 * RevenueSummaryCards
 * Props:
 *   summary      — array of card objects from API
 *   activeCardId — id of the currently active/selected card (1 or 2), or null
 *   onCardClick  — (card) => void, only called for cards with id 1 or 2
 */
function RevenueSummaryCards({ summary, activeCardId, onCardClick }) {
  if (!summary || summary.length === 0) return null;

  return (
    <div
      className="revenue-summary-row"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${Math.min(summary.length, 5)}, 1fr)`,
        gap: '12px',
        width: '100%',
        marginBottom: '4px'
      }}
    >
      {summary.map((card, idx) => {
        const style = CARD_STYLES[idx % CARD_STYLES.length];
        const isClickable = card.id === 1 || card.id === 2;
        const isActive = activeCardId === card.id;

        return (
          <div
            key={card.id || idx}
            onClick={isClickable && onCardClick ? () => onCardClick(card) : undefined}
            title={isClickable ? (isActive ? 'Click to clear filter' : 'Click to filter songs') : undefined}
            style={{
              backgroundColor: isActive ? style.bg : '#ffffff',
              borderRadius: '14px',
              border: isActive
                ? `2px solid ${style.accent}`
                : `1px solid ${style.accent}22`,
              borderLeft: isActive
                ? `4px solid ${style.accent}`
                : `4px solid ${style.accent}`,
              padding: '14px 16px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              boxShadow: isActive
                ? `0 4px 16px ${style.accent}30`
                : `0 2px 8px ${style.accent}12`,
              minHeight: '76px',
              gap: '10px',
              overflow: 'hidden',
              cursor: isClickable ? 'pointer' : 'default',
              transition: 'box-shadow 0.18s, background-color 0.18s, border-color 0.18s, transform 0.12s',
              transform: isActive ? 'translateY(-1px)' : 'translateY(0)',
              position: 'relative',
            }}
            onMouseEnter={isClickable ? (e) => {
              if (!isActive) {
                e.currentTarget.style.backgroundColor = style.bg;
                e.currentTarget.style.boxShadow = `0 4px 14px ${style.accent}25`;
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            } : undefined}
            onMouseLeave={isClickable ? (e) => {
              if (!isActive) {
                e.currentTarget.style.backgroundColor = '#ffffff';
                e.currentTarget.style.boxShadow = `0 2px 8px ${style.accent}12`;
                e.currentTarget.style.transform = 'translateY(0)';
              }
            } : undefined}
          >
            {/* Left: Labels */}
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1, minWidth: 0 }}>
              {card.title && (
                <span style={{
                  fontSize: '11.5px',
                  fontWeight: '700',
                  color: style.accent,
                  lineHeight: '1.3',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {card.title}
                </span>
              )}
              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '500', marginTop: '2px' }}>
                {card.subtitle}
              </span>
              {card.subtext && (
                <span style={{ fontSize: '10.5px', color: '#94a3b8', marginTop: '1px' }}>
                  {card.subtext}
                </span>
              )}
            </div>

            {/* Right: Value + Label */}
            <div style={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <span style={{
                fontSize: '17px',
                fontWeight: '800',
                color: '#0f172a',
                lineHeight: '1.1',
                whiteSpace: 'nowrap'
              }}>
                {card.value}
              </span>
              <span style={{
                fontSize: '10.5px',
                color: style.accent,
                fontWeight: '600',
                marginTop: '2px',
                textTransform: 'uppercase',
                letterSpacing: '0.4px'
              }}>
                {card.label}
              </span>
            </div>

            {/* Active indicator pip */}
            {isActive && (
              <div style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: style.accent,
                boxShadow: `0 0 4px ${style.accent}80`
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default RevenueSummaryCards;
